import { STORE_SCREEN } from 'utils/storeScreen';
import { leaveSignedOut } from 'utils/signedOut';
import { AcceptedSections } from '@symplbe/sympl-components';
import { SectionOrder } from 'types/careers-page/types';
import { WaitingChange } from 'api/waitingChanges';
import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import { SaveRefused } from 'hooks/useAutoSave';

/**
 * The careers page stores itself (auto-save; scenes 17 and 23, D24, D37, D42,
 * D43).
 *
 * The screen sends the WHOLE page (its sections and their order) in one
 * store. On an Active page the server KEEPS that: visitors go on seeing what
 * they saw until "Publish changes". A Draft page keeps plainly.
 */

/** What the header shows: the status, the label's count and the list. */
export interface CareersWaiting {
  status: string;
  /** Do changes wait for a publish? Only while the page is Active. */
  waits: boolean;
  count: number;
  /**
   * What differs from what visitors see or, on a page taken offline, last
   * saw. "Publish page" asks "Show me what will change?" when it is not empty.
   */
  changes: WaitingChange[];
  last_change_by: string | null;
  last_change_at: string | null;
  /** The count of stores, for the out-of-date refusal (D10). */
  kept_version?: number;
}

/** What the store is built on, and whether it is a "Keep mine". */
export interface CareersBasedOn {
  version: number | null;
  overwrite?: boolean;
}

export const contentBody = (
  content: CareersContent,
  basedOn?: CareersBasedOn
): Record<string, unknown> => ({
  ...content,
  // Which screen this store comes from (D10).
  store_screen: STORE_SCREEN,
  ...(basedOn && basedOn.version !== null ? { based_on: basedOn.version } : {}),
  ...(basedOn?.overwrite ? { overwrite: true } : {}),
});

export interface CareersContent {
  sections: Record<string, AcceptedSections>;
  order: SectionOrder[];
}

/**
 * The page as it is stored: only the sections that have a place and were not
 * deleted, and without the open jobs inside a "vacancies" section (the server
 * adds those when the page is read; they are not the person's work).
 */
export const contentToStore = (
  sections: Record<string, AcceptedSections> | undefined,
  order: SectionOrder[] | undefined,
  deletedSectionIds: string[]
): CareersContent => {
  const placed = (order ?? []).filter(
    ({ sectionId }) =>
      !deletedSectionIds.includes(sectionId) && !!sections?.[sectionId]
  );
  const stored: Record<string, AcceptedSections> = {};
  placed.forEach(({ sectionId }) => {
    const section = { ...(sections as Record<string, AcceptedSections>) }[
      sectionId
    ] as AcceptedSections & { vacancies?: unknown };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { vacancies, ...own } = section;
    stored[sectionId] = { ...own, sectionId } as AcceptedSections;
  });

  return {
    sections: stored,
    order: placed.map(({ sectionId, isHidden }) => ({
      sectionId,
      isHidden: !!isHidden,
    })),
  };
};

export const contentPath = (pageId: number | string): string =>
  `/v4/pages/${pageId}/content`;

const headers = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
  'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
});

const base = (): string => (import.meta.env.VITE_BASE_URL_API as string) ?? '';

const call = async <T>(
  path: string,
  method: 'GET' | 'POST' | 'PUT',
  what: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> => {
  const response = await fetch(`${base()}${path}`, {
    method,
    headers: headers(),
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  if (response.status === 401) {
    if (window.location.pathname !== '/') {
      leaveSignedOut();
    }
    throw new Error('The session has ended.');
  }
  // D10: the screen is out of date. Not a failure: the person chooses.
  if (response.status === 409) {
    const refused = (await response.json().catch(() => ({}))) as {
      out_of_date?: boolean;
      data?: {
        by?: string | null;
        own?: boolean;
        at?: string | null;
        kept_version?: number;
      };
    };
    if (refused.out_of_date) {
      throw new SaveRefused({
        by: refused.data?.by ?? null,
        own: refused.data?.own === true,
        at: refused.data?.at ?? null,
        keptVersion: Number(refused.data?.kept_version ?? 0),
      });
    }
  }
  if (!response.ok) throw new Error(`${what} (${response.status}).`);

  const answer = (await response.json().catch(() => ({}))) as {
    success?: boolean;
    data?: T;
  };
  if (answer.success === false || answer.data === undefined) {
    throw new Error(`${what}.`);
  }
  return answer.data;
};

/** Stores the page, quietly. @throws when the server did not confirm */
export const saveCareersContent = async (
  pageId: number | string,
  content: CareersContent,
  signal?: AbortSignal,
  basedOn?: CareersBasedOn
): Promise<CareersWaiting> => {
  const answer = await call<{ stored?: boolean; waiting: CareersWaiting }>(
    contentPath(pageId),
    'PUT',
    'The careers page was not stored',
    contentBody(content, basedOn),
    signal
  );
  if (answer.stored !== true) {
    throw new Error('The careers page was not stored.');
  }
  return answer.waiting;
};

export const getCareersWaiting = (pageId: number | string) =>
  call<CareersWaiting>(
    `/v4/pages/${pageId}/waiting-changes`,
    'GET',
    'Could not read what is waiting'
  );

/** "Publish changes" on an Active page, "Publish page" on a Draft one. */
export const publishCareers = (pageId: number | string) =>
  call<CareersWaiting>(
    `/v4/pages/${pageId}/publish`,
    'POST',
    'Could not publish'
  );

export const discardCareers = (pageId: number | string) =>
  call<CareersWaiting>(
    `/v4/pages/${pageId}/discard-changes`,
    'POST',
    'Could not discard'
  );

export const takeCareersOffline = (pageId: number | string) =>
  call<CareersWaiting>(
    `/v4/pages/${pageId}/take-offline`,
    'POST',
    'Could not take the page offline'
  );

/** The content of one earlier version, to put on the page as a change (D37). */
export const getCareersVersion = (pageId: number | string, historyId: number) =>
  call<CareersContent & { id: number }>(
    `/v4/pages/${pageId}/version-history/${historyId}`,
    'GET',
    'Could not read that version'
  );
